import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { validatePassword, passwordErrorMessage } from '../utils/passwordValidation';
import LittleLensLogo from '../components/LittleLensLogo';
import { presentationBackground } from '../theme/presentationBackground';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  useEffect(() => {
    if (!token || !emailFromUrl) {
      setError(t('resetPassword.invalidLink'));
    }
  }, [token, emailFromUrl]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordError) {
      const validation = validatePassword(value);
      setPasswordError(validation.valid ? '' : (validation.error || ''));
    }
    if (confirmError && value === passwordConfirmation) {
      setConfirmError('');
    }
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPasswordConfirmation(value);
    if (confirmError) {
      setConfirmError(value === password ? '' : t('resetPassword.passwordsDoNotMatch'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const passValidation = validatePassword(password);
    if (!passValidation.valid) {
      setPasswordError(passValidation.error || '');
      return;
    }

    if (password !== passwordConfirmation) {
      setConfirmError(t('resetPassword.passwordsDoNotMatch'));
      return;
    }

    if (!token || !emailFromUrl) {
      setError(t('resetPassword.missingTokenEmail'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword({
        email: emailFromUrl,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      setSuccess(response.message || t('resetPassword.resetSuccess'));
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('resetPassword.tooManyRequests'));
      } else {
        setError(err.response?.data?.message || t('resetPassword.resetFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        width: '100vw',
      }}
    >
      <Container maxWidth={false} sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 440, borderRadius: 5, boxShadow: '0 24px 48px rgba(15,23,42,0.16)', border: 'none' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                <LittleLensLogo maxWidth={100} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom fontWeight={800} sx={{ color: 'text.primary' }}>
                {t('resetPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('resetPassword.subtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
                {!token || !emailFromUrl ? (
                  <Box sx={{ mt: 1 }}>
                    <Link component={RouterLink} to="/forgot-password" sx={{ fontWeight: 700 }}>
                      {t('resetPassword.requestNewLink')}
                    </Link>
                  </Box>
                ) : null}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success} {t('resetPassword.redirectingToLogin')}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('resetPassword.emailLabel')}
                value={emailFromUrl || ''}
                disabled
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label={t('resetPassword.newPasswordLabel')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                required
                margin="normal"
                error={!!passwordError}
                helperText={passwordError || passwordErrorMessage}
                disabled={loading || !!success || !token}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label={t('resetPassword.confirmPasswordLabel')}
                type={showPassword ? 'text' : 'password'}
                value={passwordConfirmation}
                onChange={handleConfirmChange}
                required
                margin="normal"
                error={!!confirmError}
                helperText={confirmError}
                disabled={loading || !!success || !token}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !!success || !token || !!passwordError || !!confirmError}
                sx={{
                  mb: 3,
                  py: 1.5,
                  fontSize: '0.95rem',
                  background: presentationBackground.sidebarGradient,
                  boxShadow: '0 4px 14px rgba(73, 185, 217, 0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #269BB2 0%, #1F8499 100%)', boxShadow: '0 6px 20px rgba(73, 185, 217, 0.45)', transform: 'translateY(-1px)' },
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : t('resetPassword.resetButton')}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontWeight: 600, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  <ArrowBackIcon sx={{ fontSize: 18, mr: 1 }} />
                  {t('resetPassword.backToSignIn')}
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ResetPassword;
